/**
 * run-input-group — Compound component
 *
 * Composes run-input + run-button side-by-side as a single interactive unit.
 * Manages layout, property delegation, position attributes, validation,
 * and run-submit event emission.
 *
 * Boundary Ownership Contract v1.0: this compound claims its children's boundaries.
 * Children receive data-ys-composed and surrender their boundary treatments.
 * The group's [part="base"] element becomes the single visual boundary.
 * The group is responsible for expressing child states (focus, invalid) at
 * the boundary level — per §"Child state expression at the boundary".
 *
 * @see ./contract.md for the full interface contract
 * @see ./mvr.md for requirements
 * @see ./mva.md for architecture
 */

import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import '../input/run-input.js';
import '../button/run-button.js';

// Compound layout + boundary styles.
// The group owns the visual boundary (border, shadow, background, radius).
// Children surrender their boundaries via data-ys-composed.
const _layoutCSS = `
  :host {
    display: flex;
  }

  [part="base"] {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    width: 100%;
    gap: var(--run-input-group-gap, 0);
    /* ── Boundary treatments (Boundary Ownership Contract v1.0) ── */
    background: var(--run-input-background, var(--run-color-surface-input, #fff));
    border: 1px solid var(--run-input-border-color, var(--run-color-border-input, #d1d5db));
    border-radius: var(--run-input-border-radius, var(--run-radius-interactive-md, 6px));
    overflow: hidden;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  /* Focus — claimed from child input, expressed at the compound boundary */
  :host([data-focused]) [part="base"] {
    border-color: var(--run-input-border-color-focus, var(--run-color-border-focus, #2563eb));
    box-shadow:
      0 0 0 2px var(--run-color-surface-default, #fff),
      0 0 0 4px var(--run-input-focus-ring-color, var(--run-color-border-focus, #2563eb));
  }

  /* Invalid — claimed from child input, expressed at the compound boundary */
  :host([data-invalid]) [part="base"] {
    border-color: var(--run-input-border-color-error, var(--run-color-border-error, #dc2626));
  }
  :host([data-invalid][data-focused]) [part="base"] {
    box-shadow:
      0 0 0 2px var(--run-color-surface-default, #fff),
      0 0 0 4px var(--run-input-focus-ring-color-error, var(--run-color-border-error, #dc2626));
  }

  /* Disabled */
  :host([disabled]) [part="base"] {
    background: var(--run-input-background-disabled, var(--run-color-surface-disabled, #f3f4f6));
    border-color: var(--run-input-border-color-disabled, var(--run-color-border-disabled, #e5e7eb));
    cursor: not-allowed;
  }

  run-input {
    flex: 1 1 auto;
    min-width: 0;
  }

  run-button {
    flex: 0 0 auto;
  }

  @media (prefers-reduced-motion: reduce) {
    [part="base"] { transition: none; }
  }
`;

const _layoutSheet = new CSSStyleSheet();
_layoutSheet.replaceSync(_layoutCSS);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class RunInputGroup extends RunElement {
  // ── Private fields ──
  #type = 'email';
  #placeholder = '';
  #buttonLabel = 'Submit';
  #disabled = false;
  #loading = false;
  #value = '';
  #skin = null;
  #name = '';

  // Cached child references
  #inputEl = null;
  #buttonEl = null;

  // Bound handlers
  #handleButtonClick = null;
  #handleInputKeydown = null;
  #handleInputEvent = null;
  #handleInputChange = null;
  // State propagation: focus/invalid claimed from child, expressed at group boundary
  #handleFocusin = null;
  #handleFocusout = null;
  #handleInvalidChange = null;

  // ── Static fields ──
  static componentName = 'InputGroup';
  static version = '1.0.0';
  static category = 'compound';
  static layer = 'composite';
  static parts = ['base'];
  static slots = [];
  static events = [
    { name: 'run-submit', description: 'Fired when submit is activated and validation passes' },
  ];

  static get observedAttributes() {
    return ['type', 'placeholder', 'button-label', 'disabled', 'loading', 'value', 'skin', 'name'];
  }

  // ── Constructor ──
  constructor() {
    super();

    this.#handleButtonClick = this.#onButtonClick.bind(this);
    this.#handleInputKeydown = this.#onInputKeydown.bind(this);
    this.#handleInputEvent = this.#onInputEvent.bind(this);
    this.#handleInputChange = this.#onInputChange.bind(this);
    // focusin/focusout bubble through shadow boundaries (composed: true by spec)
    this.#handleFocusin = () => this.setAttribute('data-focused', '');
    this.#handleFocusout = () => this.removeAttribute('data-focused');
    // Mirror invalid state from the child input to the group boundary
    this.#handleInvalidChange = () => {
      const isInvalid = this.#inputEl?.hasAttribute('invalid') ?? false;
      this.toggleAttribute('data-invalid', isInvalid);
    };
  }

  // ── Lifecycle ──
  connectedCallback() {
    super.connectedCallback?.();

    // Build shadow DOM if not yet created
    if (!this.#inputEl) {
      this.#buildShadowDOM();
    }

    // Wire events
    this.#buttonEl.addEventListener('run-click', this.#handleButtonClick);
    this.#inputEl.addEventListener('keydown', this.#handleInputKeydown);
    this.#inputEl.addEventListener('run-input', this.#handleInputEvent);
    this.#inputEl.addEventListener('run-change', this.#handleInputChange);
    // State propagation: focus bubbles through shadow boundaries (composed: true)
    this.addEventListener('focusin', this.#handleFocusin);
    this.addEventListener('focusout', this.#handleFocusout);
    // Watch child invalid attribute changes via mutation observer
    this.#setupInvalidObserver();

    // Sync all props to children
    this.#syncAllToChildren();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    if (this.#buttonEl) {
      this.#buttonEl.removeEventListener('run-click', this.#handleButtonClick);
    }
    if (this.#inputEl) {
      this.#inputEl.removeEventListener('keydown', this.#handleInputKeydown);
      this.#inputEl.removeEventListener('run-input', this.#handleInputEvent);
      this.#inputEl.removeEventListener('run-change', this.#handleInputChange);
    }
    this.removeEventListener('focusin', this.#handleFocusin);
    this.removeEventListener('focusout', this.#handleFocusout);
    this.#invalidObserver?.disconnect();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'type':         this.type = newValue === null ? 'email' : newValue; break;
      case 'placeholder':  this.placeholder = newValue === null ? '' : newValue; break;
      case 'button-label': this.buttonLabel = newValue === null ? 'Submit' : newValue; break;
      case 'disabled':     this.disabled = newValue !== null; break;
      case 'loading':      this.loading = newValue !== null; break;
      case 'value':        this.value = newValue === null ? '' : newValue; break;
      case 'skin':         this.skin = newValue; break;
      case 'name':         this.name = newValue === null ? '' : newValue; break;
    }
  }

  // ── Property accessors ──

  get type() { return this.#type; }
  set type(raw) {
    const coerced = raw == null ? 'email' : String(raw).trim();
    if (this.#type === coerced) return;
    this.#type = coerced;
    this.setAttribute('type', coerced);
    this.#syncToInput();
    this.requestUpdate();
  }

  get placeholder() { return this.#placeholder; }
  set placeholder(raw) {
    const coerced = raw == null ? '' : String(raw);
    if (this.#placeholder === coerced) return;
    this.#placeholder = coerced;
    if (coerced) this.setAttribute('placeholder', coerced);
    else this.removeAttribute('placeholder');
    this.#syncToInput();
    this.requestUpdate();
  }

  get buttonLabel() { return this.#buttonLabel; }
  set buttonLabel(raw) {
    const coerced = raw == null ? 'Submit' : String(raw);
    if (this.#buttonLabel === coerced) return;
    this.#buttonLabel = coerced;
    this.setAttribute('button-label', coerced);
    this.#syncToButton();
    this.requestUpdate();
  }

  get disabled() { return this.#disabled; }
  set disabled(raw) {
    const coerced = Boolean(raw);
    if (this.#disabled === coerced) return;
    this.#disabled = coerced;
    this.toggleAttribute('disabled', coerced);
    this.#syncToInput();
    this.#syncToButton();
    this.requestUpdate();
  }

  get loading() { return this.#loading; }
  set loading(raw) {
    const coerced = Boolean(raw);
    if (this.#loading === coerced) return;
    this.#loading = coerced;
    this.toggleAttribute('loading', coerced);
    this.#syncToButton();
    this.requestUpdate();
  }

  get value() { return this.#value; }
  set value(raw) {
    const coerced = raw == null ? '' : String(raw);
    if (this.#value === coerced) return;
    this.#value = coerced;
    if (coerced) this.setAttribute('value', coerced);
    else this.removeAttribute('value');
    this.#syncToInput();
    this.requestUpdate();
  }

  get skin() { return this.#skin; }
  set skin(raw) {
    const trimmed = raw == null ? '' : String(raw).trim();
    const coerced = trimmed || null;
    if (this.#skin === coerced) return;
    this.#skin = coerced;
    if (coerced) this.setAttribute('skin', coerced);
    else this.removeAttribute('skin');
    this.#syncSkin();
    this.requestUpdate();
  }

  get name() { return this.#name; }
  set name(raw) {
    const coerced = raw == null ? '' : String(raw).trim();
    if (this.#name === coerced) return;
    this.#name = coerced;
    if (coerced) this.setAttribute('name', coerced);
    else this.removeAttribute('name');
    this.#syncToInput();
    this.requestUpdate();
  }

  // ── Public methods ──

  focus() {
    this.#inputEl?.focus();
  }

  // ── Rendering ──

  render() {
    return undefined; // Imperative DOM
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    this.shadowRoot.adoptedStyleSheets = [_layoutSheet];
    this.#syncAllToChildren();
  }

  // ── Private: Shadow DOM construction ──

  #buildShadowDOM() {
    const sr = this.shadowRoot;
    const base = document.createElement('div');
    base.setAttribute('part', 'base');
    base.setAttribute('role', 'group');

    this.#inputEl = document.createElement('run-input');
    this.#inputEl.setAttribute('hide-label', '');
    // Boundary Ownership Contract v1.0: group claims child boundaries
    this.#inputEl.setAttribute('data-ys-composed', '');

    this.#buttonEl = document.createElement('run-button');
    // Boundary Ownership Contract v1.0: group claims child boundaries
    this.#buttonEl.setAttribute('data-ys-composed', '');

    base.appendChild(this.#inputEl);
    base.appendChild(this.#buttonEl);
    sr.appendChild(base);
  }

  // ── Private: invalid state observer ──

  #invalidObserver = null;

  #setupInvalidObserver() {
    if (!this.#inputEl) return;
    this.#invalidObserver = new MutationObserver(this.#handleInvalidChange);
    this.#invalidObserver.observe(this.#inputEl, {
      attributes: true,
      attributeFilter: ['invalid'],
    });
    // Sync immediately in case the input already has invalid set
    this.#handleInvalidChange();
  }

  // ── Private: Property delegation ──

  #syncToInput() {
    if (!this.#inputEl) return;
    this.#inputEl.type = this.#type;
    this.#inputEl.placeholder = this.#placeholder;
    this.#inputEl.disabled = this.#disabled;
    this.#inputEl.name = this.#name;
    // Only sync value from compound → input when it differs from what the user typed
    if (this.#inputEl.value !== this.#value) {
      this.#inputEl.value = this.#value;
    }
  }

  #syncToButton() {
    if (!this.#buttonEl) return;
    this.#buttonEl.disabled = this.#disabled;
    this.#buttonEl.loading = this.#loading;
    this.#buttonEl.textContent = this.#buttonLabel;
  }

  #syncSkin() {
    const resolved = this.#resolveSkin();
    if (this.#inputEl) this.#inputEl.skin = resolved;
    if (this.#buttonEl) this.#buttonEl.skin = resolved;
  }

  #syncAllToChildren() {
    this.#syncToInput();
    this.#syncToButton();
    this.#syncSkin();

    // Ensure position attributes are always set
    if (this.#inputEl) this.#inputEl.setAttribute('data-position', 'start');
    if (this.#buttonEl) this.#buttonEl.setAttribute('data-position', 'end');
  }

  // ── Private: Skin resolution ──

  #resolveSkin() {
    if (this.#skin) return this.#skin;
    return resolveSkinFromAncestors(this);
  }

  // ── Private: Validation ──

  #validate() {
    const v = this.#value.trim();
    if (!v) return false;
    if (this.#type === 'email') {
      return EMAIL_RE.test(v);
    }
    return true;
  }

  // ── Private: Submit ──

  #trySubmit() {
    if (this.#disabled || this.#loading) return;
    if (!this.#validate()) return;

    this.dispatchEvent(new CustomEvent('run-submit', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: { value: this.#value.trim() },
    }));
  }

  // ── Private: Event handlers ──

  #onButtonClick(e) {
    e.stopPropagation(); // Swallow run-click — compound emits run-submit instead
    this.#trySubmit();
  }

  #onInputKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.#trySubmit();
    }
  }

  #onInputEvent(e) {
    // Sync value from child input
    const newValue = e.detail?.value ?? '';
    if (this.#value !== newValue) {
      this.#value = newValue;
      if (newValue) this.setAttribute('value', newValue);
      else this.removeAttribute('value');
    }
  }

  #onInputChange(e) {
    const newValue = e.detail?.value ?? '';
    if (this.#value !== newValue) {
      this.#value = newValue;
      if (newValue) this.setAttribute('value', newValue);
      else this.removeAttribute('value');
    }
  }
}

customElements.define('run-input-group', RunInputGroup);
